import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchForm from "@/components/SearchForm";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import copenhagenHero from "@/assets/city-copenhagen-hero.jpg";
import aarhusHero from "@/assets/city-aarhus-hero.jpg";
import odenseHero from "@/assets/city-odense-hero.jpg";
import { CalendarCheck, LockKeyhole, MapPin, Shield, Star } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

type CityConfig = {
  slug: string;
  name: string;
  intro: string;
  image: string;
  imageAlt: string;
  areaText: string;
  searchHint: string;
  highlights: string[];
  areas: string[];
  useCases: string[];
  localCopy: string;
  hostCopy: string;
  faq: { question: string; answer: string }[];
};

const cities: Record<string, CityConfig> = {
  "parkering-koebenhavn": {
    slug: "parkering-koebenhavn",
    name: "København",
    intro: "Privat parkering i hovedstaden",
    image: copenhagenHero,
    imageAlt: "Nyhavn i København med farverige bygninger, kanal og cykler",
    areaText: "Find private parkeringspladser i København, når du skal tæt på brokvarterer, metro, kontorer, studie, koncerter eller en aften i byen.",
    searchHint: "København",
    highlights: ["Tæt på bydele og metro", "Fleksible datoer", "Adresse deles efter booking"],
    areas: ["Indre By", "Vesterbro", "Nørrebro", "Østerbro", "Frederiksberg", "Amager", "Valby", "Nordhavn"],
    useCases: ["Pendling til kontor eller studie", "Aftaler i centrum uden jagt efter plads", "Koncerter, restaurantbesøg og weekendture"],
    localCopy: "I København kan parkering hurtigt blive både dyrt og tidskrævende. SharePark gør det muligt at søge efter private pladser tættere på det område, du faktisk skal til — med pris, billeder og regler samlet ét sted.",
    hostCopy: "Har du en ubrugt plads ved bolig, gård eller ejendom i København, kan den være værdifuld for pendlere, naboer og besøgende, der mangler en mere forudsigelig parkeringsmulighed.",
    faq: [
      {
        question: "Kan jeg finde parkering tæt på centrum i København?",
        answer: "Ja, du kan søge efter private pladser i og omkring centrale bydele. Udvalget afhænger af de pladser, værter har gjort ledige på dine valgte datoer.",
      },
      {
        question: "Er adressen synlig før jeg booker?",
        answer: "Nej. Af hensyn til værten vises husnummer og fuld adresse først efter en bekræftet booking.",
      },
      {
        question: "Kan jeg bruge SharePark til pendling i København?",
        answer: "Ja, mange leder efter parkering tæt på metro, stationer eller arbejdspladser. Du vælger selv datoer og ser prisen før betaling.",
      },
    ],
  },
  "parkering-aarhus": {
    slug: "parkering-aarhus",
    name: "Aarhus",
    intro: "Parkering tættere på Aarhus-livet",
    image: aarhusHero,
    imageAlt: "Aarhus havnefront med moderne arkitektur, vand og murstensbygninger",
    areaText: "Søg efter private parkeringspladser i Aarhus, når du skal til midtbyen, universitetet, havnen, hospitalet, studie eller arbejde.",
    searchHint: "Aarhus",
    highlights: ["Til studie, arbejde og besøg", "Se pris før betaling", "Sikker booking via Stripe"],
    areas: ["Aarhus C", "Frederiksbjerg", "Trøjborg", "Aarhus Ø", "Skejby", "Viby", "Risskov", "Universitetsbyen"],
    useCases: ["Besøg i midtbyen uden ekstra rundkørsel", "Parkering ved studie, hospital eller arbejde", "Plads til events, havn og kulturoplevelser"],
    localCopy: "Aarhus har mange destinationer samlet tæt, men parkering kan variere meget fra område til område. Med SharePark kan du finde private pladser i nærheden af dit egentlige mål og booke med tydelige vilkår.",
    hostCopy: "En ledig privat parkeringsplads i Aarhus kan hjælpe studerende, pendlere, patienter, gæster og lokale besøgende — samtidig med at du tjener penge på noget, der ellers står tomt.",
    faq: [
      {
        question: "Kan jeg finde parkering nær Aarhus Universitet eller Skejby?",
        answer: "Ja, søg på Aarhus og vælg dine datoer. Tilgængelige pladser afhænger af, hvad lokale værter har lagt op i området.",
      },
      {
        question: "Kan jeg se pris og regler før booking?",
        answer: "Ja. Du kan se pris, billeder, regler og relevante oplysninger, før du betaler for en parkeringsplads.",
      },
      {
        question: "Hvornår får jeg fuld adresse i Aarhus?",
        answer: "Den fulde adresse vises først efter bekræftet booking. Før booking er adressen maskeret for at beskytte værten.",
      },
    ],
  },
  "parkering-odense": {
    slug: "parkering-odense",
    name: "Odense",
    intro: "Privat parkering i Odense",
    image: odenseHero,
    imageAlt: "Historisk gade i Odense med brosten, gamle huse og varmt nordisk lys",
    areaText: "Book privat parkering i Odense til hverdagsparkering, pendling, besøg i centrum, universitetet, hospitalet eller arrangementer.",
    searchHint: "Odense",
    highlights: ["Parkering hos private", "Tydelige regler og billeder", "Fuld adresse efter booking"],
    areas: ["Odense C", "Banegården", "SDU", "OUH", "Bolbro", "Skibhuskvarteret", "Dalum", "Rosengårdcentret"],
    useCases: ["Pendling til banegård, studie eller arbejde", "Besøg i centrum uden usikkerhed om plads", "Parkering ved hospital, shopping og arrangementer"],
    localCopy: "Odense er nem at komme rundt i, men ved centrum, station, studieområder og større besøgsmål kan en fast parkeringsaftale gøre turen mere rolig. SharePark samler private pladser, så du kan vælge efter område, dato og pris.",
    hostCopy: "Har du en ubrugt plads i Odense, kan den hjælpe pendlere, studerende, hospitalsbesøgende og gæster, der gerne vil parkere mere forudsigeligt tæt på deres destination.",
    faq: [
      {
        question: "Kan jeg finde parkering tæt på Odense Banegård eller centrum?",
        answer: "Ja, du kan søge efter private pladser i Odense og se, hvad der er ledigt på dine valgte datoer i relevante områder.",
      },
      {
        question: "Er SharePark relevant til SDU eller OUH?",
        answer: "Ja, hvis lokale værter har ledige pladser i nærheden, kan du booke privat parkering til studie, arbejde eller besøg.",
      },
      {
        question: "Får jeg fuld adresse med det samme?",
        answer: "Du får fuld adresse efter bookingbekræftelse. Før det vises adressen maskeret uden husnummer.",
      },
    ],
  },
};

export default function CityParking() {
  const location = useLocation();
  const navigate = useNavigate();
  const citySlug = location.pathname.replace(/^\//, "");
  const city = cities[citySlug];

  if (!city) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold">Siden findes ikke</h1>
          <Button className="mt-6" onClick={() => navigate("/search")}>Søg parkering</Button>
        </main>
      </div>
    );
  }

  const pageUrl = `https://sharepark.dk/${city.slug}`;
  const title = `Privat parkering i ${city.name} | SharePark`;
  const description = `Find og book privat parkering i ${city.name}. Søg efter lokale områder, se priser og datoer, og få fuld adresse efter bekræftet booking.`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        keywords={`parkering ${city.name}, privat parkering ${city.name}, lej parkeringsplads ${city.name}, billig parkering ${city.name}`}
        url={pageUrl}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            ...city.faq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
            {
              "@type": "Question",
              name: `Hvordan afbestiller jeg parkering i ${city.name}?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: "Du kan afbestille efter parkeringspladsens vilkår. Eventuel refusion afhænger af tidspunktet for afbestillingen og de regler, der gælder for bookingen.",
              },
            },
          ],
        }}
      />
      <Header />
      <main>
        <section className="relative overflow-hidden bg-foreground py-16 text-primary-foreground md:py-24">
          <div className="absolute inset-0">
            <img
              src={city.image}
              alt={city.imageAlt}
              width={1600}
              height={1000}
              className="h-full w-full object-cover opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/80 to-foreground/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-foreground/15" />
          </div>
          <div className="container relative mx-auto px-4">
            <div className="max-w-4xl space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/25 bg-background/15 px-4 py-2 text-sm font-medium backdrop-blur-md">
                <MapPin className="h-4 w-4" /> {city.intro}
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight md:text-6xl">Find privat parkering i {city.name}</h1>
                <p className="max-w-2xl text-lg leading-8 text-primary-foreground/85">{city.areaText}</p>
              </div>
              <SearchForm initialLocation={city.searchHint} />
              <div className="grid gap-3 text-sm font-medium sm:grid-cols-3">
                {city.highlights.map((item) => (
                  <div key={item} className="flex items-center gap-2 rounded-full bg-background/15 px-4 py-3 backdrop-blur-md">
                    <Star className="h-4 w-4 text-warning" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14">
          <div className="container mx-auto grid gap-5 px-4 md:grid-cols-3">
            <article className="rounded-lg border bg-card p-5 shadow-sm">
              <CalendarCheck className="mb-4 h-6 w-6 text-primary" />
              <h2 className="font-semibold">Book ledige datoer</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">Vælg datoer og se prisen, før du går videre til betaling.</p>
            </article>
            <article className="rounded-lg border bg-card p-5 shadow-sm">
              <LockKeyhole className="mb-4 h-6 w-6 text-primary" />
              <h2 className="font-semibold">Adressebeskyttelse</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">Vi viser ikke husnummeret offentligt, før bookingen er bekræftet.</p>
            </article>
            <article className="rounded-lg border bg-card p-5 shadow-sm">
              <Shield className="mb-4 h-6 w-6 text-primary" />
              <h2 className="font-semibold">Tryg betaling</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">Betaling håndteres via Stripe, og bookingen verificeres bagefter.</p>
            </article>
          </div>
        </section>

        <section className="border-y bg-muted/30 py-14">
          <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">Lokale områder</p>
              <h2 className="mt-2 text-3xl font-bold">Parkering i populære områder i {city.name}</h2>
              <p className="mt-4 leading-7 text-muted-foreground">{city.localCopy}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {city.areas.map((area) => (
                  <Link
                    key={area}
                    to={`/search?query=${encodeURIComponent(`${area}, ${city.name}`)}`}
                    className="rounded-full border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                  >
                    {area}
                  </Link>
                ))}
              </div>
            </div>
            <div className="grid gap-3">
              {city.useCases.map((useCase) => (
                <div key={useCase} className="rounded-lg border bg-card p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <Shield className="mt-1 h-5 w-5 text-primary" />
                    <p className="font-medium leading-7">{useCase}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14">
          <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">For værter</p>
              <h2 className="mt-2 text-3xl font-bold">Udlej en parkeringsplads i {city.name}</h2>
              <p className="mt-4 leading-7 text-muted-foreground">{city.hostCopy}</p>
              <Button asChild className="mt-6">
                <Link to="/create-listing">Opret din plads</Link>
              </Button>
              <div className="mt-8 overflow-hidden rounded-lg border bg-card shadow-sm">
                <img
                  src={city.image}
                  alt={`${city.name} bymiljø tæt på private parkeringsmuligheder`}
                  width={800}
                  height={500}
                  loading="lazy"
                  className="h-64 w-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Ofte spurgt om parkering i {city.name}</h2>
              {city.faq.map((item) => (
                <article key={item.question} className="rounded-lg border bg-card p-5 shadow-sm">
                  <h3 className="font-semibold">{item.question}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y bg-muted/30 py-14">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold">Klar til at finde parkering i {city.name}?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Start søgningen og sammenlign private parkeringspladser efter område, datoer og pris.</p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to={`/search?query=${encodeURIComponent(city.searchHint)}`}>Søg i {city.name}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/create-listing">Udlej din plads</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
